// Pure helpers for the UoM (S5) + Templates (S8) screens. No React, no data
// fetching — grouping, preview summarisation, and problem parsing live here so
// they are exhaustively unit-tested without rendering.

import type { components } from "@assessly/api-client";
import { RequestError } from "../../../../../lib/request";

type Uom = components["schemas"]["Uom"];
type UomConversion = components["schemas"]["UomConversion"];
type CatalogueTemplate = components["schemas"]["CatalogueTemplate"];

export type UomDimension = Uom["dimension"];
export type TemplateKind = CatalogueTemplate["kind"];

/** The seven dimensions in a stable display order (spec §2.1). */
export const DIMENSIONS: readonly UomDimension[] = [
  "length",
  "mass",
  "count",
  "area",
  "volume",
  "time",
  "container",
];

/** The three template kinds in a stable display order (spec §2.17). */
export const TEMPLATE_KINDS: readonly TemplateKind[] = ["product", "bom", "routing"];

/** Hide archived rows unless the caller opted in. */
export function visibleUoms(uoms: readonly Uom[], showArchived: boolean): Uom[] {
  return showArchived ? [...uoms] : uoms.filter((u) => u.status !== "archived");
}

/** Units grouped by dimension, dimensions in canonical order, empties dropped. */
export function groupUomsByDimension(
  uoms: readonly Uom[],
): { dimension: UomDimension; uoms: Uom[] }[] {
  return DIMENSIONS.map((dimension) => ({
    dimension,
    uoms: uoms.filter((u) => u.dimension === dimension),
  })).filter((g) => g.uoms.length > 0);
}

/** True when an active base unit already exists for a dimension (spec §3.2.2:
 * the first unit in a dimension must be the base). */
export function hasActiveBase(uoms: readonly Uom[], dimension: UomDimension): boolean {
  return uoms.some((u) => u.dimension === dimension && u.is_base && u.status === "active");
}

/** Active units of a given dimension — the legal endpoints for a same-dimension
 * generic conversion (spec §2.2). */
export function activeUomsOfDimension(uoms: readonly Uom[], dimension: UomDimension): Uom[] {
  return uoms.filter((u) => u.dimension === dimension && u.status === "active");
}

export function uomCode(uoms: readonly Uom[], id: string): string | undefined {
  return uoms.find((u) => u.id === id)?.code;
}

/** Reference counts carried on a UOM_IN_USE (409) problem body, spread at the
 * top level of the RFC 9457 payload (spec §3.2.5). */
export interface UomInUse {
  products: number;
  bomLines: number;
}

/** Extract the UOM_IN_USE reference counts from a thrown archive error, or null
 * when the error is something else. */
export function parseUomInUse(error: unknown): UomInUse | null {
  if (!(error instanceof RequestError) || error.code !== "UOM_IN_USE") return null;
  const problem = error.problem as
    { references?: { products?: number; bom_lines?: number } } | undefined;
  const refs = problem?.references;
  return { products: refs?.products ?? 0, bomLines: refs?.bom_lines ?? 0 };
}

/** Prefer the server's problem title (already human-readable, e.g. "Unit code
 * already exists") over a generic i18n fallback, matching the P0 slice. */
export function messageFor(error: unknown, fallback: string): string {
  return error instanceof RequestError && error.message.length > 0 ? error.message : fallback;
}

export function visibleTemplates(
  templates: readonly CatalogueTemplate[],
  showArchived: boolean,
): CatalogueTemplate[] {
  return showArchived ? [...templates] : templates.filter((t) => t.status !== "archived");
}

/** Templates grouped by kind in canonical order; empty groups dropped, each
 * group's rows sorted by name for a stable gallery. */
export function groupTemplatesByKind(
  templates: readonly CatalogueTemplate[],
): { kind: TemplateKind; templates: CatalogueTemplate[] }[] {
  return TEMPLATE_KINDS.map((kind) => ({
    kind,
    templates: templates
      .filter((t) => t.kind === kind)
      .sort((a, b) => a.name.localeCompare(b.name)),
  })).filter((g) => g.templates.length > 0);
}

/** "Use" targets: product templates open the product create form directly;
 * BOM/routing templates need a product first, so they route to the picker. */
export function templateUseTarget(kind: TemplateKind): "product" | "structure" {
  return kind === "product" ? "product" : "structure";
}

// --- payload preview --------------------------------------------------------

export type TemplatePreviewItem =
  /** A value that is DATA and renders verbatim (a UoM code the tenant chose). */
  | { type: "value"; labelKey: string; value: string }
  /** A value drawn from a fixed ENUM, which must be translated before it is
   * shown. The gallery used to render these through `type: "value"`, putting
   * `Kind finished_good`, `Tracking measured_unit` and `Make / buy make` on the
   * screen — in Russian too (`Вид finished_good`) — while the product list one
   * click away translated the very same enum to "Finished good". The i18n
   * namespace travels with the item so the chip cannot forget to translate. */
  | { type: "enum"; labelKey: string; valueKeyPrefix: string; value: string }
  | { type: "count"; labelKey: string; count: number }
  | { type: "flag"; labelKey: string };

type Payload = CatalogueTemplate["payload"];

function readString(payload: Payload, key: string): string | undefined {
  const v = payload[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readArrayLength(payload: Payload, key: string): number | undefined {
  const v = payload[key];
  return Array.isArray(v) ? v.length : undefined;
}

function readObjectSize(payload: Payload, key: string): number | undefined {
  const v = payload[key];
  return v !== null && typeof v === "object" && !Array.isArray(v)
    ? Object.keys(v).length
    : undefined;
}

function readFlag(payload: Payload, key: string): boolean {
  return payload[key] === true;
}

const P = "catalogue.templates.preview";

/** A small, kind-aware set of preview chips for a template's payload. Returns []
 * when nothing recognisable is present (the caller shows a "no preview" line). */
export function summarizeTemplatePayload(
  kind: TemplateKind,
  payload: Payload,
): TemplatePreviewItem[] {
  const items: TemplatePreviewItem[] = [];
  const pushValue = (key: string, labelKey: string): void => {
    const value = readString(payload, key);
    if (value !== undefined) items.push({ type: "value", labelKey, value });
  };
  const pushEnum = (key: string, labelKey: string, valueKeyPrefix: string): void => {
    const value = readString(payload, key);
    if (value !== undefined) items.push({ type: "enum", labelKey, valueKeyPrefix, value });
  };

  if (kind === "product") {
    // A UoM CODE is tenant data ("m", "kg") — verbatim. The next three are
    // closed enums with names the rest of the product already translates.
    pushValue("base_uom_code", `${P}.baseUom`);
    pushEnum("kind", `${P}.productKind`, "kind");
    pushEnum("make_or_buy", `${P}.makeOrBuy`, "catalogue.productsList.makeOrBuy");
    pushEnum("tracking", `${P}.tracking`, "catalogue.tracking");
    const attrs = readObjectSize(payload, "attributes");
    if (attrs !== undefined && attrs > 0) {
      items.push({ type: "count", labelKey: `${P}.attributes`, count: attrs });
    }
  } else if (kind === "bom") {
    pushValue("output_uom_code", `${P}.outputUom`);
    const lines = readArrayLength(payload, "lines");
    if (lines !== undefined) items.push({ type: "count", labelKey: `${P}.lines`, count: lines });
    if (readFlag(payload, "is_phantom")) items.push({ type: "flag", labelKey: `${P}.phantom` });
  } else {
    const steps = readArrayLength(payload, "steps");
    if (steps !== undefined) items.push({ type: "count", labelKey: `${P}.steps`, count: steps });
  }
  return items;
}

/** SessionStorage key a create flow can read to pick up a pending template
 * selection made from the S8 gallery (integration contract, see notes). */
export const PENDING_TEMPLATE_KEY = "assessly.catalogue.pendingTemplate";

export interface PendingTemplate {
  kind: TemplateKind;
  code: string;
}

export function stashPendingTemplate(pending: PendingTemplate): void {
  try {
    window.sessionStorage.setItem(PENDING_TEMPLATE_KEY, JSON.stringify(pending));
  } catch {
    // Private-mode / storage-disabled: the navigation still lands the user in
    // the right create flow; the stash is a best-effort convenience only.
  }
}

export type { Uom, UomConversion, CatalogueTemplate };
